import { type FormEvent, useEffect, useState } from 'react';
import {
  ApiError,
  resetOwnPasswordWithMfa,
  updateOwnAccount,
  type SessionResponse,
  type User,
} from './api';

type Props = {
  csrfToken: string;
  onClose: () => void;
  onUpdated: (session: SessionResponse) => void;
  user: User;
};

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    return error.requestId === undefined
      ? error.message
      : `${error.message}（照会ID: ${error.requestId}）`;
  }
  return 'アカウント情報を更新できませんでした。';
}

export function AccountSettingsPanel({ csrfToken, onClose, onUpdated, user }: Props) {
  const [email, setEmail] = useState(user.email);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => setEmail(user.email), [user.email]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (newPassword !== confirmPassword) {
      setError('新しいパスワードと確認入力が一致しません。');
      return;
    }
    if (email.trim().toLocaleLowerCase('en-US') === user.email && newPassword === '') {
      setError('変更するメールアドレスまたは新しいパスワードを入力してください。');
      return;
    }

    setBusy(true);
    try {
      const session = await updateOwnAccount(
        {
          currentPassword,
          email: email.trim(),
          ...(newPassword === '' ? {} : { newPassword }),
        },
        csrfToken,
      );
      onUpdated(session);
      setEmail(session.user.email);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNotice('ログイン情報を更新しました。他の端末のセッションはログアウトしました。');
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  async function resetWithMfa(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (newPassword !== confirmPassword) {
      setError('新しいパスワードと確認入力が一致しません。');
      return;
    }

    setBusy(true);
    try {
      const session = await resetOwnPasswordWithMfa({ code: mfaCode, newPassword }, csrfToken);
      onUpdated(session);
      setMfaCode('');
      setNewPassword('');
      setConfirmPassword('');
      setResetMode(false);
      setNotice(
        '認証アプリで本人確認し、パスワードを再設定しました。他の端末のセッションはログアウトしました。',
      );
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="account-settings-title" className="card account-settings">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ACCOUNT SECURITY</p>
          <h2 id="account-settings-title">ログイン情報の変更</h2>
          <p className="muted">メールアドレスだけ、パスワードだけ、または両方を変更できます。</p>
        </div>
        <button className="secondary" disabled={busy} onClick={onClose} type="button">
          閉じる
        </button>
      </div>

      <div className="notice warning">
        {resetMode
          ? '現在のパスワードを忘れた管理者向けです。認証アプリの最新コードで本人確認します。'
          : '変更には現在のパスワードが必要です。変更後、この端末以外は自動的にログアウトします。'}
      </div>
      {error !== null && (
        <div aria-live="assertive" className="notice error" role="alert">
          {error}
        </div>
      )}
      {notice !== null && (
        <div aria-live="polite" className="notice success" role="status">
          {notice}
        </div>
      )}

      {user.globalRole === 'SUPER_ADMIN' && (
        <button
          className="secondary"
          disabled={busy}
          onClick={() => {
            setResetMode((current) => !current);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setMfaCode('');
            setError(null);
            setNotice(null);
          }}
          type="button"
        >
          {resetMode ? '通常の変更画面へ戻る' : '現在のパスワードを忘れた場合'}
        </button>
      )}

      {resetMode ? (
        <form
          className="form-grid account-settings-form"
          onSubmit={(event) => void resetWithMfa(event)}
        >
          <label className="span-2">
            認証アプリの6桁コード
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              minLength={6}
              onChange={(event) => setMfaCode(event.target.value)}
              pattern="[0-9]{6}"
              required
              value={mfaCode}
            />
          </label>
          <label>
            新しいパスワード
            <input
              autoComplete="new-password"
              maxLength={128}
              minLength={10}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="10文字以上"
              required
              type="password"
              value={newPassword}
            />
          </label>
          <label>
            新しいパスワード（確認）
            <input
              autoComplete="new-password"
              maxLength={128}
              minLength={10}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              type="password"
              value={confirmPassword}
            />
          </label>
          <button disabled={busy} type="submit">
            {busy ? '安全に再設定しています…' : 'MFAで確認してパスワードを再設定'}
          </button>
        </form>
      ) : (
        <form className="form-grid account-settings-form" onSubmit={(event) => void submit(event)}>
          <label className="span-2">
            新しいメールアドレス
            <input
              autoComplete="email"
              inputMode="email"
              maxLength={254}
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
          <label className="span-2">
            現在のパスワード
            <input
              autoComplete="current-password"
              maxLength={128}
              minLength={1}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          <label>
            新しいパスワード（変更する場合）
            <input
              autoComplete="new-password"
              maxLength={128}
              minLength={10}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="10文字以上"
              type="password"
              value={newPassword}
            />
          </label>
          <label>
            新しいパスワード（確認）
            <input
              autoComplete="new-password"
              maxLength={128}
              minLength={newPassword === '' ? undefined : 10}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required={newPassword !== ''}
              type="password"
              value={confirmPassword}
            />
          </label>
          <button disabled={busy} type="submit">
            {busy ? '安全に更新しています…' : 'ログイン情報を更新'}
          </button>
        </form>
      )}
    </section>
  );
}
